import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/joy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useNavigate } from 'react-router-dom';
import axios from '../util/axiosConfig';
import UserContext from '../context/UserContext';

const formatSessionStatus = (status) => {
    if (status === 'active') return 'In progress';
    if (status === 'waiting') return 'Lobby';
    return status || '-';
};

/**
 * Dashboard banner listing active live quizzes for the student's batch.
 * Renders nothing while loading or when no sessions are available.
 */
const ActiveLiveQuizBanner = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);

    const currentStudentId = user?.studentId || user?.id;

    const loadActiveQuizzes = useCallback(async () => {
        if (!currentStudentId) {
            setQuizzes([]);
            setLoading(false);
            return;
        }

        try {
            const res = await axios.get('/quizzes/active');
            if (res?.data?.success) {
                setQuizzes(Array.isArray(res.data.data) ? res.data.data : []);
            } else {
                setQuizzes([]);
            }
        } catch (e) {
            console.error('Active quizzes load error:', e);
            setQuizzes([]);
        } finally {
            setLoading(false);
        }
    }, [currentStudentId]);

    useEffect(() => {
        loadActiveQuizzes();
    }, [loadActiveQuizzes]);

    if (loading || quizzes.length === 0) {
        return null;
    }

    const joinQuiz = (sessionId) => {
        navigate(`/quiz/${encodeURIComponent(sessionId)}`);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 3,
                borderColor: '#ea7a23',
                bgcolor: '#fffaf5'
            }}
        >
            <CardContent sx={{ p: 3, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PlayCircleOutlineIcon sx={{ color: '#eb7a24' }} />
                    <Typography level="title-lg" sx={{ color: '#eb7a24' }}>
                        Live quiz available
                    </Typography>
                </Box>
                <Typography level="body-sm" sx={{ color: '#606162', mb: 2 }}>
                    A quiz session is running for your batch. Join now to participate.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {quizzes.map((quiz) => (
                        <Box
                            key={quiz.sessionId}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: 'sm',
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.surface'
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                <Typography level="title-sm">{quiz.quizTitle}</Typography>
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    Code: {quiz.sessionId}
                                </Typography>
                            </Box>
                            <Chip
                                size="sm"
                                variant="soft"
                                color={quiz.status === 'active' ? 'success' : 'warning'}
                            >
                                {formatSessionStatus(quiz.status)}
                            </Chip>
                            <Button
                                component="a"
                                href={`/quiz/${encodeURIComponent(quiz.sessionId)}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    joinQuiz(quiz.sessionId);
                                }}
                                sx={{
                                    bgcolor: '#ea7a23',
                                    '&:hover': { bgcolor: '#d06a1e' }
                                }}
                            >
                                {quiz.joined ? 'Open quiz' : 'Join quiz'}
                            </Button>
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

export default ActiveLiveQuizBanner;
